import React from "react";
import Card from "components/card";
import { FitnessData } from "../../types/user";
import {
  formatLabels,
  formatNumber,
  formatNumberClean,
} from "../../utils/formatters";
import {
  MdFitnessCenter,
  MdTrendingUp,
  MdRestaurant,
  MdDirectionsWalk,
  MdEdit,
  MdHome,
  MdWork,
} from "react-icons/md";

interface FitnessDataDisplayProps {
  fitnessData: FitnessData | null | undefined;
  onEdit?: () => void;
}

interface DataItemProps {
  label: string;
  value: string | number | null;
  suffix?: string;
}

const DataItem: React.FC<DataItemProps> = ({ label, value, suffix }) => {
  const formatValue = (val: string | number | null, suf?: string) => {
    if (val === null || val === undefined || val === "") return "Not set";
    return suf ? `${val}${suf}` : val.toString();
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
        {label}
      </label>
      <p className="text-base font-medium text-navy-700 dark:text-white">
        {formatValue(value, suffix)}
      </p>
    </div>
  );
};

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => {
  return (
    <div className="rounded-lg border border-biafit-primary/20 bg-gradient-to-br from-biafit-primary/5 to-biafit-secondary/5 p-4 dark:from-biafit-primary/10 dark:to-biafit-secondary/10">
      <div className="mb-4 flex items-center gap-2">
        <div className="rounded-lg bg-biafit-primary p-2 text-white">
          {icon}
        </div>
        <h3 className="text-base font-bold text-biafit-primary">{title}</h3>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {children}
      </div>
    </div>
  );
};

const FitnessDataDisplay: React.FC<FitnessDataDisplayProps> = ({
  fitnessData,
  onEdit,
}) => {
  if (!fitnessData) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Fitness Data
          </h2>
          {onEdit && (
            <button
              onClick={onEdit}
              className="inline-flex items-center rounded-md bg-biafit-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-biafit-secondary"
            >
              <MdEdit className="mr-2 h-4 w-4" />
              Edit Fitness Data
            </button>
          )}
        </div>
        <div className="py-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No fitness data available for this user.
          </p>
        </div>
      </Card>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const calculateWeightDifference = () => {
    if (fitnessData.start_weight && fitnessData.target_weight) {
      const diff = fitnessData.target_weight - fitnessData.start_weight;
      const formattedDiff = formatNumberClean(Math.abs(diff));
      return diff > 0 ? `+${formattedDiff} kg` : `-${formattedDiff} kg`;
    }
    return null;
  };

  return (
    <Card extra="w-full p-3 md:p-6">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold text-navy-700 dark:text-white">
          Fitness Data
        </h2>
        {onEdit && (
          <button
            onClick={onEdit}
            className="inline-flex items-center rounded-md bg-biafit-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-biafit-secondary"
          >
            <MdEdit className="mr-2 h-4 w-4" />
            Edit Fitness Data
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Training Profile Section */}
        <Section
          icon={<MdFitnessCenter className="h-5 w-5" />}
          title="Training Profile"
        >
          <DataItem
            label="Fitness Goal"
            value={formatLabels(fitnessData.fitness_goal)}
          />
          <DataItem
            label="Focus Area"
            value={formatLabels(fitnessData.focus_area)}
          />
          <DataItem
            label="Fitness Level"
            value={formatLabels(fitnessData.fitness_level)}
          />

          {/* Workout Location with Badges */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Workout Location
            </label>
            <div className="flex flex-wrap gap-2">
              {fitnessData.workout_location === "home" && (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                  <MdHome className="mr-1 h-3 w-3" />
                  Home
                </span>
              )}
              {fitnessData.workout_location === "gym" && (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                  <MdWork className="mr-1 h-3 w-3" />
                  Gym
                </span>
              )}
              {fitnessData.workout_location === "both" && (
                <>
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                    <MdHome className="mr-1 h-3 w-3" />
                    Home
                  </span>
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                    <MdWork className="mr-1 h-3 w-3" />
                    Gym
                  </span>
                </>
              )}
              {!fitnessData.workout_location && (
                <p className="text-base font-medium text-navy-700 dark:text-white">
                  Not set
                </p>
              )}
            </div>
          </div>

          <DataItem
            label="Training Frequency"
            value={fitnessData.training_frequency}
            suffix=" days/week"
          />
          <DataItem
            label="Body Composition"
            value={formatLabels(fitnessData.body_composition)}
          />
        </Section>

        {/* Weight Goals Section */}
        <Section
          icon={<MdTrendingUp className="h-5 w-5" />}
          title="Weight Goals"
        >
          <DataItem
            label="Start Weight"
            value={fitnessData.start_weight ? fitnessData.start_weight : null}
            suffix=" kg"
          />
          <DataItem
            label="Target Weight"
            value={fitnessData.target_weight ? fitnessData.target_weight : null}
            suffix=" kg"
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Weight Change Target
            </label>
            <p
              className={`text-base font-medium ${
                calculateWeightDifference()?.startsWith("+")
                  ? "text-green-600 dark:text-green-400"
                  : calculateWeightDifference()?.startsWith("-")
                  ? "text-biafit-primary"
                  : "text-navy-700 dark:text-white"
              }`}
            >
              {calculateWeightDifference() || "Not set"}
            </p>
          </div>
          <DataItem
            label="Goal Start Date"
            value={formatDate(fitnessData.start_weight_goal_date)}
          />
          <DataItem
            label="Goal End Date"
            value={formatDate(fitnessData.end_weight_goal_date)}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Timeline
            </label>
            <p className="text-base font-medium text-navy-700 dark:text-white">
              {fitnessData.start_weight_goal_date &&
              fitnessData.end_weight_goal_date
                ? `${formatNumber(
                    Math.ceil(
                      (new Date(fitnessData.end_weight_goal_date).getTime() -
                        new Date(
                          fitnessData.start_weight_goal_date
                        ).getTime()) /
                        (1000 * 60 * 60 * 24)
                    )
                  )} days`
                : "Not set"}
            </p>
          </div>
        </Section>

        {/* Nutrition Targets Section */}
        <Section
          icon={<MdRestaurant className="h-5 w-5" />}
          title="Nutrition Targets"
        >
          <DataItem
            label="Daily Calories"
            value={
              fitnessData.target_calories
                ? formatNumber(fitnessData.target_calories)
                : null
            }
            suffix=" kcal"
          />
          <DataItem
            label="Protein"
            value={
              fitnessData.target_protein
                ? formatNumber(fitnessData.target_protein)
                : null
            }
            suffix=" g/day"
          />
          <DataItem
            label="Carbohydrates"
            value={
              fitnessData.target_carbs
                ? formatNumber(fitnessData.target_carbs)
                : null
            }
            suffix=" g/day"
          />
          <DataItem
            label="Fat"
            value={
              fitnessData.target_fat
                ? formatNumber(fitnessData.target_fat)
                : null
            }
            suffix=" g/day"
          />
        </Section>

        {/* Activity Targets Section */}
        <Section
          icon={<MdDirectionsWalk className="h-5 w-5" />}
          title="Activity Targets"
        >
          <DataItem
            label="Daily Step Target"
            value={
              fitnessData.step_target
                ? formatNumber(fitnessData.step_target)
                : null
            }
            suffix=" steps"
          />
        </Section>
      </div>
    </Card>
  );
};

export default FitnessDataDisplay;
